// TanStack Query 훅 모음 — 화면 11 모든 데이터 패칭을 중앙화.
// 권위: ARCH_DASHBOARD.md §4.11 (업데이트 주기) — 리스트 30s / 밸런싱 지표 60s.

import { useQuery } from '@tanstack/react-query';
import {
  listTaxonomy,
  listMappings,
  listBalanceStats,
  listRebalanceRuns,
  listAudit,
  listFeedback,
  type MappingListQuery,
  type AuditListQuery,
  type FeedbackListQuery,
  type SectorTaxonomyNode,
  type SectorMappingListResponse,
  type SectorBalanceStatsItem,
  type RebalanceRunSummary,
  type TaxonomyAuditListResponse,
  type TaxonomyFeedbackListResponse,
} from '../../api/sectorTaxonomyApi';

const QK = {
  taxonomy: 'sector-taxonomy-list',
  mappings: 'sector-taxonomy-mappings',
  balance: 'sector-taxonomy-balance',
  rebalanceRuns: 'sector-taxonomy-rebalance-runs',
  audit: 'sector-taxonomy-audit',
  feedback: 'sector-taxonomy-feedback',
} as const;

export function useTaxonomyList(params?: {
  isActive?: boolean;
  search?: string;
}) {
  return useQuery<SectorTaxonomyNode[]>({
    queryKey: [QK.taxonomy, params ?? {}],
    queryFn: () => listTaxonomy(params),
    staleTime: 30_000,
  });
}

export function useMappingsList(query: MappingListQuery) {
  return useQuery<SectorMappingListResponse>({
    queryKey: [QK.mappings, query],
    queryFn: () => listMappings(query),
    staleTime: 30_000,
  });
}

export function useBalanceStats(from: string, to: string, metrics?: string[]) {
  return useQuery<SectorBalanceStatsItem[]>({
    queryKey: [QK.balance, from, to, metrics ?? []],
    queryFn: () => listBalanceStats(from, to, metrics),
    staleTime: 60_000,
    enabled: Boolean(from) && Boolean(to),
  });
}

export function useRebalanceRuns(limit = 10) {
  return useQuery<RebalanceRunSummary[]>({
    queryKey: [QK.rebalanceRuns, limit],
    queryFn: () => listRebalanceRuns({ limit }),
    staleTime: 30_000,
  });
}

export function useTaxonomyAudit(query: AuditListQuery) {
  return useQuery<TaxonomyAuditListResponse>({
    queryKey: [QK.audit, query],
    queryFn: () => listAudit(query),
    staleTime: 60_000,
  });
}

export function useTaxonomyFeedback(query: FeedbackListQuery) {
  return useQuery<TaxonomyFeedbackListResponse>({
    queryKey: [QK.feedback, query],
    queryFn: () => listFeedback(query),
    staleTime: 30_000,
    placeholderData: (prev) => prev,
  });
}

export const SECTOR_TAXONOMY_QK = QK;
